import { Box, Button, Divider, FormControl, MenuItem, Select, Typography } from '@mui/material';
import { FC, Fragment, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { UnitType } from 'src/api/roomService/types';
import { LoadingImage } from 'src/components/loading-image';
import { usePreloadImages } from 'src/lib/hooks/use-preload-images';
import { getCurrencySymbol } from 'src/utils/get-currency-symbol';

import { styles } from './styles';

interface RoomCardProps {
    title: string;
    roomTypes: UnitType[];
    selectedRoomType?: string;
    onRoomTypeChange: (roomType: string) => void;
    onViewRoom: (unitType: UnitType) => void;
    // Only above-the-fold cards should fetch their images with high priority
    imagePriority: 'high' | 'low';
}

export const RoomAvailabilityCard: FC<RoomCardProps> = ({
    title,
    roomTypes,
    selectedRoomType,
    onRoomTypeChange,
    onViewRoom,
    imagePriority,
}) => {
    const { t } = useTranslation();

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    // Preloading every card's images up front would be a lot of data, so wait until the user shows
    // interest in this card's gallery
    const [shouldPreloadImages, setShouldPreloadImages] = useState(false);

    const selectedUnitType =
        roomTypes.find((roomType) => roomType.name === selectedRoomType) ?? roomTypes[0];

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [selectedUnitType?.name]);

    usePreloadImages(
        shouldPreloadImages && selectedUnitType
            ? selectedUnitType.imagery.map(({ cdn_url: cdnUrl }) => cdnUrl)
            : []
    );

    if (!selectedUnitType) {
        return null;
    }

    const { imagery, room } = selectedUnitType;
    const { currency, basePrice, size } = room;
    const currencySymbol = getCurrencySymbol(currency);
    const imageCount = imagery.length;
    const currentImage = imagery[currentImageIndex];
    const hasMultipleImages = imageCount > 1;

    const showPreviousImage = () => {
        setCurrentImageIndex((currentIndex) =>
            currentIndex === 0 ? imageCount - 1 : currentIndex - 1
        );
    };

    const showNextImage = () => {
        setCurrentImageIndex((currentIndex) =>
            currentIndex === imageCount - 1 ? 0 : currentIndex + 1
        );
    };

    const handleRoomTypeChange = (roomType: string) => {
        setCurrentImageIndex(0);
        onRoomTypeChange(roomType);
    };

    return (
        <Box css={styles.roomResult}>
            <Box
                css={styles.gallery}
                onPointerEnter={() => setShouldPreloadImages(true)}
                onFocus={() => setShouldPreloadImages(true)}
            >
                {currentImage && (
                    <LoadingImage
                        key={currentImage.cdn_url}
                        src={currentImage.cdn_url}
                        alt={`${title} room`}
                        imageStyles={styles.carouselImage}
                        priority={imagePriority}
                        placeholderSrc={currentImage.thumbnail_url}
                    />
                )}

                {hasMultipleImages && (
                    <Fragment>
                        <Button
                            type="button"
                            css={[styles.carouselButton, styles.carouselButtonPrevious]}
                            aria-label={`Previous ${title} room image`}
                            onClick={showPreviousImage}
                        >
                            <ChevronLeft />
                        </Button>

                        <Button
                            type="button"
                            css={[styles.carouselButton, styles.carouselButtonNext]}
                            aria-label={`Next ${title} room image`}
                            onClick={showNextImage}
                        >
                            <ChevronRight />
                        </Button>
                    </Fragment>
                )}

                <Box css={styles.progressBar} />
            </Box>

            <Box css={styles.roomCard}>
                <Box css={styles.card}>
                    <Typography variant="h2" css={styles.title}>
                        {title}
                    </Typography>

                    <Box css={styles.titleUnderline} />

                    <Box css={styles.field}>
                        <Typography css={styles.label}>
                            {t('room_availability_card_label')}
                        </Typography>

                        <FormControl fullWidth>
                            <Select
                                value={selectedUnitType.name}
                                onChange={(event) => handleRoomTypeChange(event.target.value)}
                                css={styles.select}
                                inputProps={{
                                    'aria-label': 'Select room type',
                                }}
                            >
                                {roomTypes.map((roomType) => (
                                    <MenuItem key={roomType.name} value={roomType.name}>
                                        {roomType.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Divider css={styles.divider} />

                    <Box css={styles.detailRow}>
                        <Typography variant="h4">
                            {t('room_availability_card_size_label')}
                        </Typography>

                        <Typography variant="h4">
                            {size}
                            {t('room_availability_card_size_sqm')}
                        </Typography>
                    </Box>

                    <Divider css={styles.divider} />

                    <Box css={styles.bottomRow}>
                        <Typography variant="h3" css={styles.price}>
                            {currencySymbol}
                            {basePrice}
                            {t('room_availability_card_per_week')}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() => onViewRoom(selectedUnitType)}
                            css={styles.viewButton}
                        >
                            {t('room_availability_card_view_button')}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
